import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthFacade } from '../auth/auth.facade';
import { ToastService } from '../notification/toast.service';
import { PlansStore } from '../plans/plans.store';
import { NO_SUBSCRIPTION_CODE, SELECT_PLAN_URL } from '../plans/plans.state';
import { QUIET_FORBIDDEN, SKIP_AUTH } from './http-context-tokens';
import { EMAIL_NOT_VERIFIED_CODE } from '../auth/auth.state';
import { toAppError } from '../utils/error.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  readonly #authFacade = inject(AuthFacade);
  readonly #toast = inject(ToastService);
  readonly #router = inject(Router);
  // The store, not PlansFacade: the facade pulls in PlansApi -> HttpClient,
  // which is what constructs this interceptor. The store has no dependencies.
  readonly #plansStore = inject(PlansStore);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // The auth endpoints authenticate with the refresh cookie rather than the
    // header, and their 401 is the answer, not something to retry. Touching
    // them here is how a refresh loop starts.
    if (req.context.get(SKIP_AUTH)) return next.handle(req);

    return this.#refreshIfDue().pipe(
      switchMap(() => this.#send(req, next)),
    );
  }

  /**
   * Rotates a token that is about to expire before the request goes out, so the
   * user never pays for the 401-then-retry detour. A refresh that fails here is
   * not fatal — the request still goes out and the 401 path below takes over.
   */
  #refreshIfDue(): Observable<unknown> {
    if (!this.#authFacade.isAuthenticated() || !this.#authFacade.needsRefresh()) {
      return of(null);
    }
    return this.#authFacade.refreshShared().pipe(catchError(() => of(null)));
  }

  #send(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(this.#authorize(req)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && this.#authFacade.isAuthenticated()) {
          return this.#retryAfterRefresh(req, next, error);
        }
        if (error.status === 403) {
          this.#explainForbidden(error, req.context.get(QUIET_FORBIDDEN));
        } else if (this.#isNoSubscription(error)) {
          this.#sendToPlanSelection();
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * One refresh — shared with every other request that hit a 401 at the same
   * moment — then a single retry with the new token. A refresh that fails means
   * the session is genuinely gone, so the user goes back to the login page.
   *
   * The retry is deliberately outside the error handling: a second 401 must
   * surface to the caller rather than start the cycle over.
   */
  #retryAfterRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    return this.#authFacade.refreshShared().pipe(
      map(result => result.ok),
      catchError(() => of(false)),
      switchMap(refreshed => {
        if (refreshed) return next.handle(this.#authorize(req));
        void this.#authFacade.logout('session_expired');
        return throwError(() => error);
      }),
    );
  }

  #authorize(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.#authFacade.getToken();
    return token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;
  }

  /**
   * Every 403 is explained here, once, in the server's own words — an
   * unverified email and an insufficient organization role are both 403 and ask
   * different things of the user. Callers stay quiet on `Forbidden` rather than
   * guessing a second message on top of this one.
   *
   * `email_not_verified` is not an auth failure: the session is good and the
   * account may keep reading. Marking the claim raises the gate even for a
   * token minted before the claim existed, which reads as verified.
   *
   * A {@link QUIET_FORBIDDEN} request renders its own 403 (the AI upsell, say),
   * so it gets no toast on top.
   */
  #explainForbidden(error: HttpErrorResponse, quiet: boolean): void {
    if (error.error?.error === EMAIL_NOT_VERIFIED_CODE) {
      this.#authFacade.markEmailUnverified();
    }
    if (quiet) return;
    this.#toast.showError(toAppError(error).message);
  }

  /**
   * A 402 the backend tagged `no_subscription`: the org has nothing to bill
   * against, so no endpoint will serve it. Narrowed to that code on purpose —
   * a 402 from a payment attempt is a declined card, not a paywall.
   */
  #isNoSubscription(error: HttpErrorResponse): boolean {
    return error.status === 402 && error.error?.code === NO_SUBSCRIPTION_CODE;
  }

  /**
   * The safety net behind the subscription guard: it catches an org whose
   * subscription lapses mid-session, where no navigation would otherwise
   * re-run the guard.
   */
  #sendToPlanSelection(): void {
    if (!this.#authFacade.isAuthenticated()) return;
    this.#plansStore.markNoSubscription();
    if (this.#router.url.startsWith(SELECT_PLAN_URL)) return;
    void this.#router.navigate([SELECT_PLAN_URL]);
  }
}
